import { Stack } from "@mui/material";
import { FacilityPhotos } from "Components/Shared/Merchant/Facilities/FacilityPhotos";
import { ValueItems } from "Components/Shared/ValuesBox/ValueItems";
import { ValuesBox } from "Components/Shared/ValuesBox/ValuesBox";
import type { MerchantFacilityDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { formatCurrency } from "Utils/CurrencyUtils";
import { dtoToState, formatDayHours } from "Utils/OpeningHoursUtils";
import { formatPhone } from "Utils/PhoneUtils";
import { WeekDay } from "Types/WeekDay";

const COMPONENT_RESOURCES = Resources.Components.Merchant.Facilities.Form;

const ORDERED_WEEKDAYS = [
  WeekDay.Monday,
  WeekDay.Tuesday,
  WeekDay.Wednesday,
  WeekDay.Thursday,
  WeekDay.Friday,
  WeekDay.Saturday,
  WeekDay.Sunday,
];

type Props = {
  merchantPublicID: string;
  facility: Partial<MerchantFacilityDto>;
};

export const FacilityDetail = ({ merchantPublicID, facility }: Props) => {
  const { t } = useResource();
  const hoursState = dtoToState(facility.hours ?? []);
  // Turnover figures render in the facility's own stored currency
  // (TurnoverCurrencyCode — stamped from the merchant's market at creation).
  const currencyCode = facility.turnoverCurrencyCode;

  return (
    <Stack gap={2}>
      <ValuesBox title={t(COMPONENT_RESOURCES.Legend.BasicInformation)}>
        <ValueItems
          values={[
            { label: t(COMPONENT_RESOURCES.Fields.Name), value: facility.name },
            {
              label: t(COMPONENT_RESOURCES.Fields.ProvidedServices),
              value: facility.providedServices,
            },
          ]}
        />
      </ValuesBox>
      <ValuesBox title={t(COMPONENT_RESOURCES.Legend.Contact)}>
        <ValueItems
          values={[
            {
              label: t(COMPONENT_RESOURCES.Fields.ResponsiblePerson),
              value: facility.responsiblePerson,
            },
            {
              label: t(COMPONENT_RESOURCES.Fields.Phone),
              value: formatPhone(facility.phone),
            },
            {
              label: t(COMPONENT_RESOURCES.Fields.Email),
              value: facility.email,
            },
          ]}
        />
      </ValuesBox>
      <ValuesBox title={t(COMPONENT_RESOURCES.Legend.OpeningHours)}>
        <ValueItems
          values={ORDERED_WEEKDAYS.map((day) => ({
            label: t(Resources.Common.WeekDay[day]),
            value: formatDayHours(hoursState[day], t),
          }))}
        />
      </ValuesBox>
      <ValuesBox title={t(COMPONENT_RESOURCES.Legend.Address)}>
        <ValueItems
          values={[
            {
              label: t(COMPONENT_RESOURCES.Fields.Address.Street),
              value: facility.address?.street,
            },
            {
              label: t(COMPONENT_RESOURCES.Fields.Address.StreetNumber),
              value: facility.address?.streetNumber,
            },
            {
              label: t(COMPONENT_RESOURCES.Fields.Address.OrientationNumber),
              value: facility.address?.orientationNumber,
            },
            {
              label: t(COMPONENT_RESOURCES.Fields.Address.PostalCode),
              value: facility.address?.postalCode,
            },
            {
              label: t(COMPONENT_RESOURCES.Fields.Address.Municipality),
              value: facility.address?.municipality,
            },
          ]}
        />
      </ValuesBox>
      <ValuesBox title={t(COMPONENT_RESOURCES.Legend.Turnover)}>
        <ValueItems
          values={[
            {
              label: t(COMPONENT_RESOURCES.Fields.YearlyTurnover),
              value: formatCurrency({
                amount: facility.yearlyTurnover,
                currencyCode,
              }),
            },
            {
              label: t(COMPONENT_RESOURCES.Fields.RealCardTurnover),
              value: formatCurrency({
                amount: facility.realCardTurnover,
                currencyCode,
              }),
            },
            {
              label: t(COMPONENT_RESOURCES.Fields.EstimatedTurnover),
              value: formatCurrency({
                amount: facility.estimatedTurnover,
                currencyCode,
              }),
            },
            {
              label: t(COMPONENT_RESOURCES.Fields.AverageTransactionValue),
              value: formatCurrency({
                amount: facility.averageTransactionValue,
                currencyCode,
              }),
            },
          ]}
        />
      </ValuesBox>
      <FacilityPhotos merchantPublicID={merchantPublicID} facility={facility} />
    </Stack>
  );
};
